import React, { useEffect, useState, useRef } from 'react'
import DatePicker from 'react-datepicker'
import { format, startOfDay, endOfDay, isEqual } from 'date-fns'
import { parseDate } from 'helpers/date_input_helper'

import 'react-datepicker/dist/react-datepicker.css'

import './style.scss'

export default function ReactDateTimePicker({
  inputName,
  disableHiddenInputName = false,
  currentValue,
  showTimeSelect,
  submitTime,
  minDate,
  maxDate,
  id,
  disabled,
  readonly,
  className = 'react-datepicker',
  errorMessage,
  placeholderText,
  format: dateFormat,
  showMonthYearPicker,
  prefilled
}) {
  const date = parseDate(currentValue)
  const [startDate, setStartDate] = useState(date)
  const [hiddenDate, setHiddenDate] = useState('')
  const [invalidRangeMessage, setInvalidRangeMessage] = useState()
  const realInput = useRef(null)
  const rawInputValue = useRef('')

  useEffect(() => {
    setHiddenDate(formatDateForHidden(startDate))

    window.dispatchEvent(
      new CustomEvent('react-date-time:mounted', {
        bubbles: true,
        detail: {
          inputName,
          id
        }
      })
    )

    window.addEventListener('react-date-time:force-change', handleForceChange)

    return () => {
      window.removeEventListener('react-date-time:force-change', handleForceChange)
    }
  }, [])

  const updateDates = date => {
    setInvalidRangeMessage()
    setStartDate(date)
    setHiddenDate(formatDateForHidden(date))
  }

  const handleForceChange = event => {
    if (event.detail && id.endsWith(event.detail.id)) {
      const newDate = parseDate(event.detail.date)
      updateDates(newDate)
    }
  }

  const handleRawChange = event => {
    rawInputValue.current = event.target.value
  }

  const dateChanged = (date, inputName) => {
    // Handles date changes from the DatePicker component. If the user manually typed an ISO date (YYYY-MM-DD),
    // we parse that raw input instead of using the date object from the picker to preserve the exact date entered
    // (avoiding timezone conversion issues).
    if (rawInputValue.current && /^\d{4}-\d{2}-\d{2}$/.test(rawInputValue.current)) {
      date = parseDate(rawInputValue.current)
    }

    updateDates(date)
    const formattedDate = formatDateForHidden(date)
    var customEvent = new CustomEvent('react-date-time:changed', {
      detail: {
        date,
        formattedDate,
        inputName,
        id
      }
    })
    realInput.current.dispatchEvent(customEvent)

    // Deprecated: Event should be dispatched from the input element itself, but we have many things depending on the
    // event being dispatched from the window.
    //
    window.dispatchEvent(customEvent)
  }

  const formatDateForHidden = date => {
    if (!date) {
      return undefined
    } else if (showTimeSelect) {
      return format(date, 'MMMM d, yyyy h:mm aaa')
    } else if (submitTime) {
      return `${format(date, 'MMMM d, yyyy')} 12:00 PM`
    } else {
      return format(date, 'MMMM d, yyyy')
    }
  }

  const fireChangeEvent = () => {
    const initialDate = date
    const changeDate = startDate
    if (initialDate && changeDate && !isEqual(initialDate, changeDate)) {
      // Fire a change event on the hidden input after the update is complete.
      realInput.current.dispatchEvent(new CustomEvent('change', { bubbles: true }))
    }
    if ((!initialDate && changeDate) || (initialDate && !changeDate)) {
      realInput.current.dispatchEvent(new CustomEvent('change', { bubbles: true }))
    }
  }

  const validDateInRange = ({ target: { value } }) => {
    if (!value) {
      return
    }
    const dateValue = new Date(value)

    if (isNaN(dateValue)) {
      setInvalidRangeMessage(`“${value}” is an invalid date—review value above`)
    } else {
      if (minDate) {
        const minDateParsed = parseDate(minDate)
        if (minDateParsed && dateValue < startOfDay(minDateParsed)) {
          setInvalidRangeMessage(`“${value}” is not within the accepted date range—review value above`)
        }
      }

      if (maxDate) {
        const maxDateParsed = parseDate(maxDate)
        if (maxDateParsed && dateValue > endOfDay(maxDateParsed)) {
          setInvalidRangeMessage(`“${value}” is not within the accepted date range—review value above`)
        }
      }
    }
  }

  return (
    <div>
      <DatePicker
        selected={startDate}
        onChange={date => dateChanged(date, inputName)}
        onChangeRaw={handleRawChange}
        showTimeSelect={showTimeSelect}
        submitTime={submitTime}
        timeFormat='h:mm aa'
        timeIntervals={15}
        timeCaption='time'
        dateFormat={dateFormat || (showTimeSelect ? 'MMMM d, yyyy h:mm aa' : 'MMMM d, yyyy')}
        name={inputName}
        className={`react-date-time ${className} ${errorMessage && 'is-invalid'}`}
        minDate={parseDate(minDate)}
        maxDate={parseDate(maxDate) || new Date(2100, 0, 1)}
        id={id}
        autoComplete='off'
        disabled={disabled || readonly}
        showMonthDropdown
        showYearDropdown
        showMonthYearPicker={showMonthYearPicker}
        dropdownMode='select'
        fixedHeight
        popperPlacement='bottom'
        placeholderText={placeholderText}
        onBlur={validDateInRange}
        onCalendarClose={fireChangeEvent}
      />
      {invalidRangeMessage && <div className='d-block invalid-feedback'>{invalidRangeMessage}</div>}
      {errorMessage && <div className='d-block invalid-feedback'>{errorMessage}</div>}
      <input
        type='hidden'
        className='react-date-time'
        name={disableHiddenInputName ? null : inputName}
        value={hiddenDate ? hiddenDate : ''}
        disabled={disabled}
        ref={realInput}
        data-prefilled={prefilled}
      />
      <div className='d-block text-warning' id={id + '_warning'} />
    </div>
  )
}
